import { Modal } from './Modal.js';
import { confirmDialog } from './confirmDialog.js';
import * as SaveState from './SaveState.js';
import {
    isMusicMuted,
    isSfxMuted,
    setMusicMuted,
    setSfxMuted,
} from './audioSettings.js';

export interface OptionsWindowOptions {
    /** When set, the Restart button is shown and clears this game's save. */
    gameId?: string;
}


export class OptionsWindow {
    private readonly modal: Modal;
    private readonly gameId: string | undefined;
    private readonly list: HTMLElement;
    private readonly musicCheckbox: HTMLInputElement;
    private readonly sfxCheckbox: HTMLInputElement;

    constructor({ gameId }: OptionsWindowOptions = {}) {
        this.modal = new Modal({ title: 'OPTIONS', variant: 'options' });
        this.gameId = gameId;

        // modal.body is a flex row with overflow:hidden, so rows must live
        // inside one stacked child (as every other window does).
        this.list = document.createElement('div');
        this.list.className = 'options-list';
        this.modal.body.appendChild(this.list);

        this.musicCheckbox = this.buildCheckboxRow('Music', () => {
            setMusicMuted(!this.musicCheckbox.checked);
        });

        this.sfxCheckbox = this.buildCheckboxRow('Sound effects', () => {
            setSfxMuted(!this.sfxCheckbox.checked);
        });

        if (gameId) this.buildRestartRow();
    }

    private buildCheckboxRow(label: string, onChange: () => void): HTMLInputElement {
        const row = document.createElement('label');
        row.className = 'options-row';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.addEventListener('change', onChange);
        row.appendChild(checkbox);

        const text = document.createElement('span');
        text.textContent = label;
        row.appendChild(text);

        this.list.appendChild(row);
        return checkbox;
    }

    private buildRestartRow(): void {
        const row = document.createElement('div');
        row.className = 'options-row options-row--restart';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'options-restart';
        button.textContent = 'Restart game';
        button.addEventListener('click', () => void this.confirmRestart());
        row.appendChild(button);

        this.list.appendChild(row);
    }

    private async confirmRestart(): Promise<void> {
        if (!this.gameId) return;

        const confirmed = await confirmDialog({
            title: 'Restart game',
            message: 'Restart the game from Level 1? Your current progress will be lost.',
            confirmLabel: 'Restart',
        });
        if (!confirmed) return;

        // Suppress saves first: the reload fires pagehide, and persist would
        // otherwise resurrect the save we just cleared.
        SaveState.suppressSaves();
        SaveState.clear(this.gameId);
        window.location.reload();
    }

    open(): void {
        this.musicCheckbox.checked = !isMusicMuted();
        this.sfxCheckbox.checked = !isSfxMuted();
        this.modal.open();
    }

    close(): void {
        this.modal.close();
    }

    toggle(): void {
        if (this.modal.isOpen) this.close();
        else this.open();
    }
}
